import { mapAndExclude } from "@/utils/array-utils";
import {
  Link,
  linkOptions,
  ToOptions,
  useMatch,
  useMatches,
} from "@tanstack/react-router";
import { Fragment, useMemo } from "react";
import { twMerge } from "tailwind-merge";
import { z } from "zod";

const withCrumbSchema = z.object({
  isBreadcrumbRoot: z.boolean().optional(),
  crumb: z.string(),
});

type Props = {
  className?: string;
  from: ToOptions["from"];
  fromCrumb?: string;
  size?: "sm" | "md";
};

// https://github.com/TanStack/router/blob/main/examples/react/kitchen-sink-file-based/src/components/Breadcrumbs.tsx
export function Breadcrumbs({
  className,
  from,
  fromCrumb,
  size = "md",
}: Props) {
  const currentMatch = useMatch({ from: "__root__" });

  const textSize = size === "sm" ? "text-xs" : "text-sm";

  const matches = useMatches();

  const scopedMatches = useMemo(() => {
    let fromIndex: number | null = null;
    let toIndex = matches.length;

    for (const [index, match] of matches.entries()) {
      if (fromIndex === null) {
        if (match.fullPath === from) {
          fromIndex = index;
          continue;
        }
      } else {
        const { data: loaderData } = withCrumbSchema.safeParse(
          match.loaderData,
        );

        if (
          (loaderData?.isBreadcrumbRoot ?? match.staticData.isBreadcrumbRoot) &&
          match.fullPath !== from
        ) {
          toIndex = index + 1;
          break;
        }
      }
    }

    if (fromIndex === null) {
      return [];
    }

    return matches.slice(fromIndex, toIndex);
  }, [from, matches]);

  const crumbs = useMemo(
    () =>
      mapAndExclude(scopedMatches, (exclude, match) => {
        const { staticData, loaderData, fullPath } = match;

        for (const object of [loaderData, staticData]) {
          const { data } = withCrumbSchema.safeParse(object);
          if (data) {
            let title = data.crumb;
            if (fullPath === from && fromCrumb) {
              title = fromCrumb;
            }
            return {
              title,
              link: linkOptions({
                from: currentMatch.fullPath,
                to: String(match.fullPath),
                params: match.params,
                search: match.search,
              }),
            };
          }
        }
        return exclude;
      }).slice(-4),
    [currentMatch.fullPath, from, fromCrumb, scopedMatches],
  );

  const reversedCrumbs = useMemo(() => [...crumbs].reverse(), [crumbs]);

  if (matches.some((match) => match.status === "pending")) return null;

  return (
    <nav
      className={twMerge(
        "flex min-w-0 flex-col text-[11px] font-medium",
        className,
      )}
    >
      <ul className="-m-2 truncate overflow-clip p-2 [direction:rtl]">
        {reversedCrumbs.map((crumb, i) => (
          <Fragment key={i}>
            <li
              className={twMerge(
                "text-theme-on-background first:text-theme-active inline font-medium",
                textSize,
              )}
            >
              <Link className="inline" {...crumb.link}>
                {crumb.title}
              </Link>
            </li>

            {i < crumbs.length - 1 && (
              <div
                className={twMerge("text-theme-outline inline px-2", textSize)}
              >
                /
              </div>
            )}
          </Fragment>
        ))}
      </ul>
    </nav>
  );
}
